import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import {
  FolderTree,
  Plus,
  Save,
  Trash2,
  Pencil,
  Search,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const TallyLedgerSubGroupMas = () => {
  const { groupId, groupName } = useParams();
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [subGroupName, setSubGroupName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [subGroups, setSubGroups] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showForm, setShowForm] = useState(true);

  const pageSize = 5;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchSubGroups = async () => {
    try {
      const res = await axios.get(
        "http://nissi-india.com/api/getTallyLedgerSubGroups",
        {
          params: {
            groupid: groupId,
            search,
            page,
            pageSize,
          },
          headers: commonHeaders,
        }
      );

      setSubGroups(res.data.subGroupList || []);
      setTotalRecords(res.data.totalRecords || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch sub groups");
    }
  };

  useEffect(() => {
    if (groupId) {
      fetchSubGroups();
    }
  }, [groupId, search, page, commonHeaders]);

  const resetForm = () => {
    setSubGroupName("");
    setRemarks("");
    setEditingId(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!subGroupName.trim()) {
      toast.error("Sub Group Name is required");
      return;
    }

    try {
      if (editingId) {
        await axios.put(
          `http://nissi-india.com/api/updateTallyLedgerSubGroup/${editingId}`,
          {
            groupid: parseInt(groupId, 10),
            subgroupname: subGroupName,
            remarks,
          },
          { headers: commonHeaders }
        );
        toast.success("Sub group updated successfully");
      } else {
        await axios.post(
          "http://nissi-india.com/api/insertTallyLedgerSubGroup",
          {
            groupid: parseInt(groupId, 10),
            subgroupname: subGroupName,
            remarks,
            userid: parseInt(userId, 10) || 0,
          },
          { headers: commonHeaders }
        );
        toast.success("Sub group added successfully");
      }

      resetForm();
      fetchSubGroups();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Error saving sub group");
    }
  };

  const handleEdit = (sub) => {
    setSubGroupName(sub.subgroupname || "");
    setRemarks(sub.remarks || "");
    setEditingId(sub.subgroupid);
    setShowForm(true);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Select sub groups to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete selected sub groups?")) {
      return;
    }

    try {
      await axios.delete(
        "http://nissi-india.com/api/deleteTallyLedgerSubGroups",
        {
          data: { subgroupids: selectedIds },
          headers: commonHeaders,
        }
      );

      toast.success("Sub groups deleted successfully");
      setSelectedIds([]);
      fetchSubGroups();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to delete sub groups");
    }
  };

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const decodedGroupName = decodeURIComponent(groupName || "");

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Tally Ledger Sub Group</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FolderTree className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {editingId ? "Edit Sub Group" : "Add New Sub Group"}
                </h3>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Parent Group: <span className="font-semibold">{decodedGroupName}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {showForm && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Parent Group</label>
                  <input
                    type="text"
                    className={`${inputClass} opacity-70 cursor-not-allowed`}
                    value={decodedGroupName}
                    disabled
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Sub Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={subGroupName}
                    onChange={(e) => setSubGroupName(e.target.value)}
                    placeholder="Enter sub group name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={resetForm}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} />
                  Cancel / New
                </button>

                <button
                  onClick={handleSave}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <Save size={16} />
                  {editingId ? "Update Sub Group" : "Save Sub Group"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Sub Group List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} records
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search sub group..."
                    className={`pl-9 pr-3 py-2 rounded-lg border text-sm w-56 ${
                      isDark
                        ? "bg-slate-800/60 border-slate-600 text-white"
                        : "bg-white border-slate-300"
                    }`}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>

                <button
                  onClick={handleDelete}
                  disabled={selectedIds.length === 0}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
                    selectedIds.length > 0
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <Trash2 size={16} />
                  Delete ({selectedIds.length})
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Sub Group Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Remarks</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {subGroups.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <FolderTree size={40} className="text-gray-400" />
                        <span>No sub groups found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  subGroups.map((sub, idx) => {
                    const isSelected = selectedIds.includes(sub.subgroupid);

                    return (
                      <tr
                        key={sub.subgroupid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""}`}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(sub.subgroupid)}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-4 py-3 font-medium">{sub.subgroupname}</td>
                        <td className="px-4 py-3">{sub.remarks || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(sub)}
                            className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing {totalRecords === 0 ? 0 : (page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, totalRecords)} of {totalRecords} results
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page <= 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border-x border-gray-300 dark:border-gray-700">
                  Page {page} of {totalPages}
                </span>

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page >= totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default TallyLedgerSubGroupMas;